import {
  Component,
  effect,
  ElementRef,
  EventEmitter,
  inject,
  OnDestroy,
  Output,
  signal,
  ViewChild,
} from '@angular/core';
import { form, FormField, minLength, required } from '@angular/forms/signals';
import { AddImageComponent } from '../../../../atoms/add-image/add-image.component';
import { S3BucketEnum } from '../../../../common/general-types/media-assets.dto';
import { CreateBusinessAccountDto } from '@/app/common/general-types/users.dto';
import { FirebaseService } from '@/app/common/firebase/firebase.service';
import { CurrentUserService } from '@/app/common/current-user/current-user.service';
import { BusinessAccountService } from '@/app/services/business-account/business-account.service';
import { SlickOnboardAgency } from '../../service/slick-onboard-agency/slick-onboard-agency';
import { ToastNotificationService } from '@/app/atoms/toast-notification/toast-notification.service';
import { BusinessAccountDto } from '@/app/common/general-types/business-account.dto';
import { catchError, debounceTime, finalize, Subject, Subscription, EMPTY, switchMap, of } from 'rxjs';
import { BrandFetchService, BrandSuggestionResponse } from '@/app/pages/onboarding/service/brand-fetch/brand-fetch.service';
import { BrandDetailCardIndustry, BrandDetailCardLink, BrandDetailCardModel } from '@/app/pages/onboarding-v2/steps/brand-details/brand-details';
import { ViewportOverlayDirective } from '@/app/common/directives/viewport-overlay/viewport-overlay.directive';

export type BrandFormModel = {
  brandName: string;
  brandWebsite: string;
  brandDescription: string;
  jobPosition: string;
  toolOnly: boolean;
  brandLogo: string;
};

/** Narrow Brandfetch.io v2 brand JSON into fields we render. */
function normalizeBrandfetchDetail(raw: unknown): BrandDetailCardModel {
  if (!raw || typeof raw !== 'object') {
    return { company: { industries: [] }, links: [] };
  }
  const r = raw as Record<string, unknown>;

  const description = typeof r['description'] === 'string' ? r['description'] : '';
  const longDescription = typeof r['longDescription'] === 'string' ? r['longDescription'] : '';
  const name = typeof r['name'] === 'string' ? r['name'] : undefined;
  const domain = typeof r['domain'] === 'string' ? r['domain'] : undefined;

  let industries: BrandDetailCardIndustry[] = [];
  const company = r['company'];
  if (company && typeof company === 'object') {
    const indRaw = (company as Record<string, unknown>)['industries'];
    if (Array.isArray(indRaw)) {
      industries = indRaw
        .map((item, i) => {
          if (!item || typeof item !== 'object') return undefined;
          const it = item as Record<string, unknown>;
          const id = String(it['id'] ?? `ind-${i}`);
          const nm = typeof it['name'] === 'string' ? it['name'] : '';
          const emoji = typeof it['emoji'] === 'string' ? it['emoji'] : '';
          return { id, name: nm, emoji };
        })
        .filter((x): x is BrandDetailCardIndustry => x !== undefined && x.name.length > 0);
    }
  }

  let links: BrandDetailCardLink[] = [];
  const linksRaw = r['links'];
  if (Array.isArray(linksRaw)) {
    links = linksRaw.map((item, i) => {
      if (!item || typeof item !== 'object') {
        return { name: `Link ${i + 1}` };
      }
      const L = item as Record<string, unknown>;
      const linkName =
        typeof L['name'] === 'string'
          ? L['name']
          : typeof L['type'] === 'string'
            ? L['type']
            : `Social ${i + 1}`;
      const url = typeof L['url'] === 'string' ? L['url'] : undefined;
      return { name: linkName, url };
    });
  }

  return {
    name,
    domain,
    description,
    longDescription,
    company: { industries },
    links,
  };
}

@Component({
  selector: 'oba-agency-brand-add',
  imports: [FormField, AddImageComponent],
  hostDirectives: [ViewportOverlayDirective],
  templateUrl: './agency-brand-add.html',
  styleUrl: './agency-brand-add.scss',
  host: {
    '(document:keydown.escape)': 'closeBrandModal()'
  }
})
export class AgencyBrandAdd implements OnDestroy {

  @Output() closeModal = new EventEmitter<void>();
  @Output() brandAdded = new EventEmitter<BusinessAccountDto>();

  @ViewChild('brandDomainInput') brandDomainInput?: ElementRef<HTMLInputElement>;

  readonly S3BucketEnum = S3BucketEnum;

  entryModeManual = signal<boolean>(true);
  savingBrand = signal<boolean>(false);
  selectedTab = signal<'manual' | 'smart'>('manual');

  /** Services */
  private firebaseService = inject(FirebaseService);
  private currentUserService = inject(CurrentUserService);
  private businessAccountService = inject(BusinessAccountService);
  private slickOnboardAgencyService = inject(SlickOnboardAgency);
  private toastNotificationService = inject(ToastNotificationService);
  private brandFetchService = inject(BrandFetchService);
  /** Services - END */


  brandFormModel = signal<BrandFormModel>({
    brandName: '',
    brandWebsite: '',
    brandDescription: '',
    jobPosition: '',
    toolOnly: false,
    brandLogo: '',
  });

  brandForm = form(this.brandFormModel, (schemaPath) => {
    required(schemaPath.brandName, { message: 'Brand name is required' });
    minLength(schemaPath.brandName, 2, { message: 'Brand name must be at least 2 characters long' });
    required(schemaPath.brandWebsite, { message: 'Brand website is required' });
    required(schemaPath.brandDescription, { message: 'Brand description is required' });
  });

  brandFetchModel = signal({
    domain: '',
  });
  brandFetchForm = form(this.brandFetchModel);
  private domainSubject = new Subject<string>();
  private subscriptions = new Subscription();

  brandSuggestions = signal<BrandSuggestionResponse[] | undefined>(undefined);
  brandSearchLoading = signal(false);
  brandDetailFetchPending = signal(false);

  selectedBrand = signal<BrandSuggestionResponse | undefined>(undefined);

  brandDetails = signal<BrandDetailCardModel | undefined>(undefined);

  constructor() {
    this.subscriptions.add(
      this.domainSubject
        .pipe(
          debounceTime(250),
          switchMap((domain) => {
            const trimmed = domain.trim();
            if (!trimmed || trimmed.length <= 3) {
              this.brandSearchLoading.set(false);
              this.brandSuggestions.set(undefined);
              return EMPTY;
            }
            this.brandSearchLoading.set(true);
            return this.brandFetchService.brandSearch(trimmed).pipe(
              catchError(() => of([] as BrandSuggestionResponse[])),
              finalize(() => this.brandSearchLoading.set(false)),
            );
          }),
        )
        .subscribe((response) => {
          this.brandSuggestions.set(response);

          if (response.length === 1) {
            this.onBrandSelect(response[0]);
          }
        }),
    );

    effect(() => {
      const domain = this.brandFetchModel().domain;
      this.domainSubject.next(domain);
    });
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  onBrandSelect(brand: BrandSuggestionResponse): void {
    console.log('[AgencyBrandAdd] Selected brand (search suggestion):', brand);

    this.selectedBrand.set(brand);

    this.brandDetailFetchPending.set(true);
    this.subscriptions.add(
      this.brandFetchService.getBrandDetails(brand.brandId).subscribe({
        next: (response: unknown) => {
          const card = normalizeBrandfetchDetail(response);
          this.brandDetails.set(card);
          this.brandDetailFetchPending.set(false);
          console.log('[AgencyBrandAdd] Selected brand (full details):', {
            suggestion: brand,
            normalizedCard: card,
            rawBrandfetchResponse: response,
          });

          this.brandFormModel.update((m) => ({ ...m, brandName: card.name || '', brandWebsite: card.domain || '', brandDescription: card.description || '', brandLogo: brand.icon || '' }));
        },
        error: () => {
          const card: BrandDetailCardModel = {
            description: '',
            company: { industries: [] },
            links: [],
          };
          this.brandDetails.set(card);
          this.brandDetailFetchPending.set(false);
          console.log('[AgencyBrandAdd] Brand detail fetch failed; suggestion only:', brand);
        },
      }),
    );
  }

  onBrandSuggestionKeydown(event: KeyboardEvent, brand: BrandSuggestionResponse): void {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      this.onBrandSelect(brand);
    }
  }

  saveBrand() {
    console.log('saveBrand()');
    console.log('currentUserService.focussedAgency: ', this.currentUserService.focussedAgency);
    this.savingBrand.set(true);

    const businessInfoReq: CreateBusinessAccountDto = {
      companyName: this.brandFormModel().brandName,
      website: this.brandFormModel().brandWebsite || '',
      instagram: '',
      tiktok: '',
      about: this.brandFormModel().brandDescription || '',
      coverPhoto: this.brandFormModel().brandLogo || '',
      companyLogo: this.brandFormModel().brandLogo || '',
      creatorId: this.firebaseService.userId || '',
      isActive: true,
      currencyCode: this.slickOnboardAgencyService.agencyFormModel().currencyCode,
      isPayg: false,
      industries: [],
      vatNumber: '',
      agencyId: this.currentUserService.focussedAgency.id || '',
      stripeCustomerId: this.currentUserService.focussedAgency.stripeCustomerId || '',
      toolOnly: this.brandFormModel().toolOnly || false,
    };

    console.log('businessInfoReq: ', businessInfoReq);

    this.businessAccountService.addBrandToAgencyOnboard(businessInfoReq).subscribe((res: any) => {
      console.log('addBrandToAgencyOnboard: ', res);
      this.savingBrand.set(false);
      this.toastNotificationService.alert({
        title: 'Brand added successfully',
        message: 'Brand added successfully',
        type: 'success',
        icon: 'success'
      });
      this.brandAdded.emit(res.business);
    }, (error: any) => {
      console.error('Add Agency Brand Error: ', error);
      this.savingBrand.set(false);
      this.toastNotificationService.alert({
        title: 'Error adding brand',
        message: 'Error adding brand',
        type: 'error',
        icon: 'error'
      });
    });
  }

  onBrandLogoUploaded(event: { file: string; fileKey: string; fullUrl: string }): void {
    this.brandFormModel.update((m) => ({ ...m, brandLogo: event.fileKey }));
  }

  closeBrandModal() {
    this.closeModal.emit();
  }

  getTabClass(tab: 'manual' | 'smart') {
    return this.selectedTab() === tab
      ? 'bg-gray-200 text-gray-900 dark:bg-white/10 dark:text-gray-200'
      : 'text-gray-500 hover:text-gray-900 dark:text-gray-400 dark:hover:text-gray-200';
  }

  resetBrandSearch(): void {
    this.selectedBrand.set(undefined);
    this.brandDetails.set(undefined);
    this.brandSuggestions.set(undefined);
    this.brandDetailFetchPending.set(false);
    this.brandFetchModel.set({ domain: '' });
    this.domainSubject.next('');
    queueMicrotask(() => this.brandDomainInput?.nativeElement?.focus());
  }

}
